import type { WineType } from "@corklog/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  reportForProducer,
  reportForRegion,
  useAreaIndex,
  type AreaLink,
} from "../area-links.js";

/**
 * The published area reports that describe a wine's region or its house —
 * task 10.36. Nothing at all where none does, and nothing for a reader who is
 * not shown *Upptäck*: a quiet line that is there when it has somewhere to
 * lead.
 *
 * **The region's report first, and the house's only when it is another** —
 * a Bollinger from Champagne leads to the one report once, not twice.
 */
export function AreaReportLinks({
  producer,
  region,
  countryCode,
  kind,
}: {
  producer: string;
  region: string | null;
  countryCode: string;
  kind: WineType;
}) {
  const { t } = useTranslation();
  const index = useAreaIndex();
  if (index === null) return null;

  const links: AreaLink[] = [];
  const byRegion =
    region === null ? null : reportForRegion(index, countryCode, region, kind);
  if (byRegion !== null) links.push(byRegion);
  const byProducer = reportForProducer(index, producer);
  if (byProducer !== null && byProducer.id !== byRegion?.id) links.push(byProducer);
  if (links.length === 0) return null;

  return (
    <p className="mt-1 flex flex-wrap gap-x-4 text-sm">
      {links.map((link) => (
        <Link
          key={link.id}
          to={`/upptack/${encodeURIComponent(link.id)}`}
          className="text-accent underline-offset-4 hover:underline"
        >
          {t("discover.readAbout", { area: link.area })}
        </Link>
      ))}
    </p>
  );
}
